import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, Loader2Icon, UserIcon, DatabaseIcon, ShieldIcon, SettingsIcon } from 'lucide-react';
import { StepProps } from '../../types/onboarding';
interface ReviewCreateProps extends StepProps {
  onBack: () => void;
}
export function ReviewCreate({
  data,
  setCanAdvance,
  onBack
}: ReviewCreateProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  useEffect(() => {
    setCanAdvance(true);
  }, [setCanAdvance]);
  const handleCreate = () => {
    setIsCreating(true);
    setTimeout(() => {
      setIsCreating(false);
      setIsSuccess(true);
    }, 1500);
  };
  const isLoanOfficer = data.profile.includes('Loan Officer');
  const isProcessor = data.role.includes('Processor');
  const hasEinstein = data.permissionSets.includes('Loan Officer Permission Set') || data.permissionSets.includes('Processor Permission Set');
  const DataRow = ({
    label,
    value
  }: {
    label: string;
    value: any;
  }) => <div className="py-2 flex justify-between border-b border-gray-100 last:border-0" data-id="element-310">
      <span className="text-sm text-gray-500" data-id="element-311">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right max-w-[60%] truncate" data-id="element-312">
        {value || <span className="text-gray-400 italic" data-id="element-313">None</span>}
      </span>
    </div>;
  if (isSuccess) {
    return <div className="py-12 text-center flex flex-col items-center justify-center min-h-[400px]" data-id="element-314">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="w-20 h-20 bg-[#2E844A] rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-315">
          <CheckIcon className="w-10 h-10 text-white" data-id="element-316" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-2xl font-bold text-gray-900 mb-2" data-id="element-317">
          User Successfully Created!
        </motion.h2>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="text-sm text-gray-600 mb-10" data-id="element-318">
          {data.firstName} {data.lastName} ({data.username}) has been fully
          onboarded.
        </motion.p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10 text-left w-full max-w-2xl" data-id="element-319">
          {[{
          icon: UserIcon,
          count: 1,
          label: 'User'
        }, {
          icon: DatabaseIcon,
          count: 1,
          label: 'Account'
        }, {
          icon: ShieldIcon,
          count: data.permissionSets.length,
          label: 'Perm Sets'
        }, {
          icon: SettingsIcon,
          count: isLoanOfficer ? 4 : 3,
          label: 'Metadata'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + i * 0.1
        }} className="bg-white border border-gray-100 rounded-xl p-4 flex items-center space-x-4 shadow-sm" data-id="element-320">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-321">
                <item.icon className="w-5 h-5 text-[#2563EB]" data-id="element-322" />
              </div>
              <div data-id="element-323">
                <div className="text-xl font-bold text-gray-900 leading-none" data-id="element-324">
                  {item.count}
                </div>
                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1" data-id="element-325">
                  {item.label}
                </div>
              </div>
            </motion.div>)}
        </div>

        <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} onClick={() => window.location.reload()} className="px-6 py-2.5 bg-white border border-gray-300 text-sm text-gray-700 font-medium rounded-full shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2563EB] transition-colors" data-id="element-326">
          Start New Onboarding
        </motion.button>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-327">
      <div className="flex-1 space-y-8" data-id="element-328">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-329">
          <div data-id="element-330">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-331">
              User & Account
            </h4>
            <DataRow label="Name" value={`${data.firstName} ${data.lastName}`} data-id="element-332" />
            <DataRow label="Username" value={data.username} data-id="element-333" />
            <DataRow label="Profile" value={data.profile} data-id="element-334" />
            <DataRow label="Role" value={data.role} data-id="element-335" />
          </div>
          <div data-id="element-336">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-337">
              Operational
            </h4>
            <DataRow label="Team" value={data.team} data-id="element-338" />
            <DataRow label="Call Center" value={data.callCenter} data-id="element-339" />
            <DataRow label="States" value={data.statesLicensed.length ? `${data.statesLicensed.length} selected` : 'None'} data-id="element-340" />
            <DataRow label="Groups" value={data.publicGroups.length ? `${data.publicGroups.length} selected` : 'None'} data-id="element-341" />
          </div>
          <div data-id="element-342">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-343">
              Access
            </h4>
            <DataRow label="Packages" value={data.packageLicenses.length ? `${data.packageLicenses.length} selected` : 'None'} data-id="element-344" />
            <DataRow label="Perm Sets" value={data.permissionSets.length ? `${data.permissionSets.length} selected` : 'None'} data-id="element-345" />
          </div>
          <div data-id="element-346">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2 mb-3" data-id="element-347">
              Automated Metadata
            </h4>
            <ul className="text-sm text-gray-700 space-y-1.5 list-disc list-inside py-2" data-id="element-348">
              <li data-id="element-349">Round Robin Rules</li>
              <li data-id="element-350">Shift Definitions</li>
              <li data-id="element-351">Commissions ({isProcessor ? 'Processor' : 'Standard'})</li>
              {isLoanOfficer && <li data-id="element-352">Weighting Rules</li>}
              {hasEinstein && <li data-id="element-353">Einstein Activity Capture</li>}
            </ul>
          </div>
        </div>
      </div>

      {/* Inline Navigation for Review Step */}
      <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-354">
        <button onClick={onBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:underline transition-colors" data-id="element-355">
          Previous
        </button>

        <button onClick={handleCreate} disabled={isCreating} className="flex items-center justify-center px-8 py-2.5 bg-[#2563EB] text-white text-sm font-medium rounded-full shadow-sm hover:bg-[#1D4ED8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2563EB] disabled:opacity-70 disabled:cursor-not-allowed transition-all" data-id="element-356">
          {isCreating ? <>
              <Loader2Icon className="w-4 h-4 mr-2 animate-spin" data-id="element-357" />
              Creating User...
            </> : 'Create User'}
        </button>
      </div>
    </div>;
}